import React, { forwardRef } from 'react';
import { MsgType, Room } from 'matrix-js-sdk';
import { HTMLReactParserOptions } from 'html-react-parser';
import { Opts } from 'linkifyjs';
import {
  Box,
  Button,
  Dialog,
  config,
  Overlay,
} from 'folds';
import {
  AudioContent,
  DownloadFile,
  FileContent,
  ImageContent,
  MAudio,
  MBadEncrypted,
  MEmote,
  MFile,
  MImage,
  MLocation,
  MNotice,
  MText,
  MVideo,
  ReadPdfFile,
  ReadTextFile,
  RefofReadPdfFile,
  RenderBody,
  ThumbnailContent,
  UnsupportedContent,
  VideoContent,
} from './message';
import { UrlPreviewCard, UrlPreviewHolder } from './url-preview';
import { Image, MediaControl, Video } from './media';
import { ImageViewer } from './image-viewer';
import { PdfViewer } from './Pdf-viewer';
import { TextViewer } from './text-viewer';
import { testMatrixTo } from '../plugins/matrix-to';

import { actStrFixing, actStrSatiSurvey, actStrRepairOrder, CurveCmdDialogVerify, CurveCmdAIActionVerify, CurveGetRepairOrderInfo, CurveCmdsVerify } from '../utils/CurveCmds';

import { RepairOrder } from './message/content/RepairOrder';

import './index.css';

type RenderMessageContentProps = {
  displayName: string;
  msgType: string;
  ts: number;
  edited?: boolean;
  getContent: <T>() => T;
  mediaAutoLoad?: boolean;
  urlPreview?: boolean;
  highlightRegex?: RegExp;
  htmlReactParserOptions: HTMLReactParserOptions;
  linkifyOpts: Opts;
  outlineAttachment?: boolean;
  msgId?: string;
  room?: Room;
  setShowDocArea: React.Dispatch<React.SetStateAction<boolean>>;
  mid: string;
  setMid: React.Dispatch<React.SetStateAction<string>>;
  setDocInfo: React.Dispatch<React.SetStateAction<unknown>>;
};
export const RenderMessageContent = forwardRef<HTMLDivElement, RenderMessageContentProps>(({
  displayName,
  msgType,
  ts,
  edited,
  getContent,
  mediaAutoLoad,
  urlPreview,
  highlightRegex,
  htmlReactParserOptions,
  linkifyOpts,
  outlineAttachment,
  msgId,
  room,
  setShowDocArea,
  mid,
  setMid,
  setDocInfo
}, ref) => {
  const renderUrlsPreview = (urls: string[]) => {
    const filteredUrls = urls.filter((url) => !testMatrixTo(url));
    if (filteredUrls.length === 0) return undefined;
    return (
      <UrlPreviewHolder>
        {filteredUrls.map((url) => (
          <UrlPreviewCard key={url} url={url} ts={ts} />
        ))}
      </UrlPreviewHolder>
    );
  };

  const renderFile = () => (
    <MFile
      content={getContent()}
      renderFileContent={({ body, mimeType, info, encInfo, url }) => (
        <FileContent
          body={body}
          mimeType={mimeType}
          renderAsPdfFile={() => (
            <ReadPdfFile
              body={body}
              mimeType={mimeType}
              url={url}
              encInfo={encInfo}
              room={room}
              msgId={msgId}
              renderViewer={(p) => <PdfViewer {...p} />}
              setShowDocArea={setShowDocArea}
              mid={mid}
              setMid={setMid}
              setDocInfo={setDocInfo}
              ref={ref}
            />
          )}
          renderAsTextFile={() => (
            <ReadTextFile
              body={body}
              mimeType={mimeType}
              url={url}
              encInfo={encInfo}
              renderViewer={(p) => <TextViewer {...p} />}
            />
          )}
        >
          <DownloadFile body={body} mimeType={mimeType} url={url} encInfo={encInfo} info={info} />
        </FileContent>
      )}
      outlined={outlineAttachment}
    />
  );

  if (msgType === MsgType.Text) {
    const { body } = getContent() as Record<string, unknown>;
    if (typeof body === 'string') {
      if (CurveCmdDialogVerify(body, actStrFixing )) {
        return (<Box><Button>{actStrFixing}</Button></Box>);
      }
      
      if (CurveCmdAIActionVerify(body, actStrSatiSurvey)) {
        return (
          <Dialog variant="Surface">
            <Box style={{ padding: config.space.S400 }} direction="Column" gap="400">
              <Box direction="Column" gap="200">
                {'這是你要問的問題嗎?'}
              </Box>
            </Box>
            <Button variant="Primary" style={{margin: '10px'}}>{'Yes'}</Button>
            <Button variant="Primary" style={{margin: '10px', float: 'right'}}>{'No'}</Button>
          </Dialog>
        );
      } 
      
      if (CurveCmdAIActionVerify(body, actStrRepairOrder)) {
        const RepairInfo = CurveGetRepairOrderInfo(body);
        if(!RepairInfo) return null;
        return (<RepairOrder RepairInfo={RepairInfo} mid={mid} setMid={setMid} msgId={msgId} setDocInfo={setDocInfo} body={body} setShowDocArea={setShowDocArea}/>);
        /*
        return (
            <Box grow="Yes" style={{width: '800px'}}>
              <Overlay open className={'RepairOrder'} style={{marginLeft: '850px', zIndex: '10'}} >
                <table>
                  <tbody>
                    {Object.entries(RepairInfo).map((item)=> (item[0] === 'command' || item[0] === 'agent response') ? null : <tr key={item[0]}><th>{itemName[item[0]]}</th><td>{item[1]}</td></tr>)}
                  </tbody>
                </table>
              </Overlay>
            </Box>
        );
        */
      }

      // eslint-disable-next-line consistent-return
      if (CurveCmdsVerify(body)) return;
    }

    return (
      <MText
        edited={edited}
        content={getContent()}
        renderBody={(props) => (
          <RenderBody
            {...props}
            highlightRegex={highlightRegex}
            htmlReactParserOptions={htmlReactParserOptions}
            linkifyOpts={linkifyOpts}
          />
        )}
        renderUrlsPreview={urlPreview ? renderUrlsPreview : undefined}
      />
    );
  }

  if (msgType === MsgType.Emote) {
    return (
      <MEmote
        displayName={displayName}
        edited={edited}
        content={getContent()}
        renderBody={(props) => (
          <RenderBody
            {...props}
            highlightRegex={highlightRegex}
            htmlReactParserOptions={htmlReactParserOptions}
            linkifyOpts={linkifyOpts}
          />
        )}
        renderUrlsPreview={urlPreview ? renderUrlsPreview : undefined}
      />
    );
  }

  if (msgType === MsgType.Notice) {
    return (
      <MNotice
        edited={edited}
        content={getContent()}
        renderBody={(props) => (
          <RenderBody
            {...props}
            highlightRegex={highlightRegex}
            htmlReactParserOptions={htmlReactParserOptions}
            linkifyOpts={linkifyOpts}
          />
        )}
        renderUrlsPreview={urlPreview ? renderUrlsPreview : undefined}
      />
    );
  }

  if (msgType === MsgType.Image) {
    return (
                  <MImage
                    content={getContent()}
                    renderImageContent={(props) => (
                      <ImageContent
                        {...props}
                        autoPlay={mediaAutoLoad}
                        renderImage={(p) => (
                          <Image
                            {...p}
                            loading="lazy"
                            onClick={() => {
                              const requestClose = () => {
                                setDocInfo({});
                                setShowDocArea(false);
                              };
                              setDocInfo({
                                imageViewer: true,
                                renderViewer: (viewerProps) => <ImageViewer {...viewerProps} />,
                                body: p.alt,
                                src: p.src,
                                requestClose,
                              });
                              setShowDocArea(true);
                            }}
                            style={{ cursor: 'pointer' }}
                          />
                        )}
                        renderViewer={() => null} // Prevent ImageContent from rendering its own viewer
                      />
                    )}
                    outlined={outlineAttachment}
                  />    );
  }
/* TMS: not to support now
  if (msgType === MsgType.Video) {
    return (
      <MVideo
        content={getContent()}
        renderAsFile={renderFile}
        renderVideoContent={({ body, info, mimeType, url, encInfo }) => (
          <VideoContent
            body={body}
            info={info}
            mimeType={mimeType}
            url={url}
            encInfo={encInfo}
            renderThumbnail={
              mediaAutoLoad
                ? () => (
                    <ThumbnailContent
                      info={info}
                      renderImage={(src) => (
                        <Image alt={body} title={body} src={src} loading="lazy" />
                      )}
                    />
                  )
                : undefined
            }
            renderVideo={(p) => <Video {...p} />}
          />
        )}
        outlined={outlineAttachment}
      />
    );
  }

  if (msgType === MsgType.Audio) {
    return (
      <MAudio
        content={getContent()}
        renderAsFile={renderFile}
        renderAudioContent={(props) => (
          <AudioContent {...props} renderMediaControl={(p) => <MediaControl {...p} />} />
        )}
        outlined={outlineAttachment}
      />
    );
  }
*/
  if (msgType === MsgType.File) {
    return renderFile();
  }

  if (msgType === MsgType.Location) {
    return <MLocation content={getContent()} />;
  }

  if (msgType === 'm.bad.encrypted') {
    return <MBadEncrypted />;
  }

  return <UnsupportedContent />;
});
